import { useState } from 'react'
import type { Order } from '@/api/types'
import { useUser } from '@/auth/useAuth'
import { can } from '@/auth/rbac'
import { Button, ConfirmSheet, Sheet } from '@/ui'
import { useCancelOrder, useCreateOrderQR, useOrderLifecycle, type LifecycleAction } from './queries'

interface ActionDef {
  action: LifecycleAction
  label: string
  capability: 'orders.manage' | 'orders.control'
  confirmTitle: string
}

// Which lifecycle transitions are offered for each status (mirrors the backend state machine).
const ACTIONS_BY_STATUS: Record<Order['lifecycle_status'], ActionDef[]> = {
  DRAFT: [
    { action: 'submit', label: 'Отправить в работу', capability: 'orders.manage', confirmTitle: 'Отправить заказ?' },
  ],
  PLANNED: [
    { action: 'launch', label: 'Запустить', capability: 'orders.control', confirmTitle: 'Запустить производство?' },
    { action: 'return-to-draft', label: 'В черновик', capability: 'orders.control', confirmTitle: 'Вернуть в черновик?' },
  ],
  IN_PRODUCTION: [
    { action: 'pause', label: 'Пауза', capability: 'orders.control', confirmTitle: 'Поставить на паузу?' },
  ],
  PAUSED: [
    { action: 'resume', label: 'Возобновить', capability: 'orders.control', confirmTitle: 'Возобновить производство?' },
  ],
  READY: [
    { action: 'ship', label: 'Отгрузить', capability: 'orders.control', confirmTitle: 'Отгрузить заказ?' },
  ],
  SHIPPED: [],
  CANCELLED: [],
}

const CANCELLABLE: Order['lifecycle_status'][] = [
  'DRAFT',
  'PLANNED',
  'IN_PRODUCTION',
  'PAUSED',
  'READY',
]

export function OrderLifecycleBar({ order }: { order: Order }) {
  const user = useUser()
  const lifecycle = useOrderLifecycle(order.id)
  const cancel = useCancelOrder(order.id)
  const qr = useCreateOrderQR(order.id)

  const [pending, setPending] = useState<ActionDef | null>(null)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)

  const actions = ACTIONS_BY_STATUS[order.lifecycle_status].filter((a) =>
    can(user.role, a.capability),
  )
  const canCancel = CANCELLABLE.includes(order.lifecycle_status) && can(user.role, 'orders.control')

  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((a) => (
        <Button key={a.action} size="md" onClick={() => setPending(a)}>
          {a.label}
        </Button>
      ))}
      <Button
        size="md"
        variant="secondary"
        onClick={() => {
          qr.reset()
          setQrOpen(true)
          qr.mutate()
        }}
      >
        QR
      </Button>
      {canCancel && (
        <Button size="md" variant="danger" onClick={() => setCancelOpen(true)}>
          Отменить
        </Button>
      )}

      {pending && (
        <ConfirmSheet
          open
          onClose={() => setPending(null)}
          title={pending.confirmTitle}
          confirmLabel={pending.label}
          loading={lifecycle.isPending}
          error={lifecycle.error}
          onConfirm={() =>
            lifecycle.mutate(pending.action, { onSuccess: () => setPending(null) })
          }
        />
      )}

      <ConfirmSheet
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Отменить заказ?"
        description="Действие нельзя отменить. Все незавершённые этапы будут отменены."
        confirmLabel="Отменить заказ"
        confirmVariant="danger"
        loading={cancel.isPending}
        error={cancel.error}
        reason={{ label: 'Причина отмены', required: true, placeholder: 'Например: клиент отказался' }}
        onConfirm={(reason) => cancel.mutate(reason, { onSuccess: () => setCancelOpen(false) })}
      />

      <Sheet open={qrOpen} onClose={() => setQrOpen(false)} title="QR / deep-link заказа">
        {qr.isPending && <p className="text-sm text-tg-hint">Генерация…</p>}
        {qr.data && (
          <div className="space-y-2">
            <p className="text-sm text-tg-hint">Токен для быстрого открытия заказа:</p>
            <code className="block break-all rounded-lg bg-tg-secondary-bg px-3 py-2 text-xs">
              {qr.data.token}
            </code>
          </div>
        )}
        {qr.error && <p className="text-sm text-risk-red">Не удалось получить токен</p>}
      </Sheet>
    </div>
  )
}
